import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  CheckCircle2,
  DatabaseZap,
  FileText,
  Filter,
  FolderPlus,
  List,
  Plus,
  Search,
  Trees,
} from 'lucide-react';
import type { ArticleSummary, TextbookCatalogNode, TextbookSummary } from '@en3/shared';
import { contentApi } from '@/api/content.api';
import { queryKeys } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { useAuthStatus, useAuthUser } from '@/api/hooks/useAuth';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/card';
import { EmptyBlock, ErrorBlock, LoadingBlock, Pagination } from '@/components/common/States';
import { ArticleCard, ArticleEmptyHint, FormSelect } from '@/components/content/ArticleCard';
import { TextbookTree, filterCatalogNodes } from '@/components/content/TextbookTree';
import { ImportArticleDialog } from '@/components/content/ImportArticleDialog';
import { CreateTextbookDialog } from '@/components/content/CreateTextbookDialog';
import { CreateUnitDialog } from '@/components/content/CreateUnitDialog';

/**
 * 教材目录页。
 *
 * 三个结构性决定，都是为了"手机上一眼能用"：
 *
 * 1. **整棵树一次请求拿完**（`catalog({ withUnits: true })`），标题搜索在本地做。
 *    树只有几十到几百个节点，本地匹配没有延迟、不打断输入；而「全部文章」是分页的，
 *    本地过滤看不到第二页，所以那边的关键词搜索走后端 `q`。
 *    一句话：**能看到的数据本地过滤，看不到的数据服务端过滤。**
 *
 * 2. **两个视图（目录 / 全部文章）共用一个 level 筛选。**
 *    用户切换视图时心智模型没变 ——"我在看大学英语三" —— 所以筛选值不重置。
 *    level 输入用 400ms 防抖后才进查询键，否则每敲一个字符都要打一次接口。
 *
 * 3. **权限按钮按角色出现，而不是出现后再报 403。**
 *    teacher/admin 才有「新建教材 / 新建单元」，只有 admin 有「装载内置目录」。
 *    后端仍然各自校验一次 —— 前端隐藏是体验，不是安全边界。
 */

const DEFAULT_LEVEL = '大学英语三';
const ARTICLES_PAGE_SIZE = 20;
const EXPANDED_STORAGE_KEY = 'en3.catalog.expandedTextbooks';

type ViewMode = 'tree' | 'articles';
type Scope = 'all' | 'public' | 'me';

const SCOPE_OPTIONS: { value: Scope; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'public', label: '公开' },
  { value: 'me', label: '我的' },
];

/** 形状收窄：带 units 的才是目录树节点（见下面 `nodes` 处的说明）。 */
function hasUnits(node: TextbookSummary | TextbookCatalogNode): node is TextbookCatalogNode {
  return 'units' in node;
}

export function TextbookCatalogPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthUser()?.role;
  const status = useAuthStatus();
  const canManage = role === 'teacher' || role === 'admin';
  const isAdmin = role === 'admin';

  const [view, setView] = useState<ViewMode>('tree');
  const [levelInput, setLevelInput] = useState(DEFAULT_LEVEL);
  /** 防抖后的 level：只有它进查询键 */
  const [level, setLevel] = useState(DEFAULT_LEVEL);
  const levelTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [keyword, setKeyword] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [createTextbookOpen, setCreateTextbookOpen] = useState(false);
  const [createUnitOpen, setCreateUnitOpen] = useState(false);
  const [unitTextbookId, setUnitTextbookId] = useState<string | undefined>(undefined);
  const [seedNotice, setSeedNotice] = useState<string | null>(null);

  function changeLevel(next: string) {
    setLevelInput(next);
    if (levelTimer.current) clearTimeout(levelTimer.current);
    levelTimer.current = setTimeout(() => setLevel(next.trim()), 400);
  }

  const listParams = useMemo(() => ({ level: level || undefined, withUnits: true }), [level]);
  const catalogQuery = useQuery({
    queryKey: queryKeys.textbooks(listParams),
    queryFn: () => contentApi.catalog({ ...listParams }),
    // 登录态一致性问题由 api/client 统一处理（请求会先等 bootstrap 完成），这里不再各自判断
  });

  // 后端 `withUnits: true` 的响应形状就是 TextbookCatalogNode[]。
  //
  // `contentApi.catalog` 的返回类型写成 `TextbookCatalogNode[] | TextbookSummary[]` 的并集
  // （同一个端点既能返回平面列表也能返回树），所以这里用 `hasUnits` 把形状对一遍。
  // 刻意**不用裸 `as` 断言**：真出现"响应里没有 units"的情况时，
  // 断言会让 `node.units` 变成 undefined，错误会推迟到渲染时炸掉整页；
  // 过滤掉不合法项则退化成空目录（配一条空态提示），用户至少还能看到页面。
  const rawItems = catalogQuery.data?.items;
  const nodes: TextbookCatalogNode[] = (rawItems ?? []).filter(hasUnits);

  // 展开状态：默认第一本展开，用户的手动开关记到 localStorage。
  // 记住展开状态的价值在于"我昨天看到 Unit 5，今天打开还在那儿"。
  const [expandedIds, setExpandedIds] = useState<Set<string> | null>(() => readExpanded());

  /**
   * 「从未选择过时默认展开第一本」用**派生**实现，而不是在 effect 里 setState。
   *
   * 之前的写法是 effect 里 setState：目录到达 → effect 跑 → 再渲染一轮；而且依赖里带着
   * `nodes`，会出现"用户刚把第一本收起来、目录一刷新又被展开"的抢状态问题。
   *
   * 现在 `expandedIds === null` 专门表示"用户没选择过"，选择过（哪怕是空集合）就以他为
   * 准 —— 语义清楚了，渲染也只有一轮。
   */
  const effectiveExpandedIds = useMemo(() => {
    if (expandedIds !== null) return expandedIds;
    const first = nodes[0];
    return first ? new Set<string>([first.id]) : new Set<string>();
  }, [expandedIds, nodes]);

  const toggleTextbook = useCallback(
    (id: string) => {
      const next = new Set(effectiveExpandedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setExpandedIds(next);
      writeExpanded(next);
    },
    [effectiveExpandedIds],
  );

  const filteredNodes = useMemo(() => filterCatalogNodes(nodes, keyword), [nodes, keyword]);
  const keywordActive = keyword.trim().length > 0;

  const openArticle = useCallback(
    (article: ArticleSummary) => {
      navigate(ROUTES.reader(article.id));
    },
    [navigate],
  );

  const seedMutation = useMutation({
    mutationFn: () => contentApi.seedTextbooks(),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['textbooks'] });
      setSeedNotice(
        data.created > 0
          ? `已装载内置目录：新增 ${data.created} 项（共 ${data.textbooks} 套教材、${data.units} 个单元）。`
          : `内置目录已是最新：${data.textbooks} 套教材、${data.units} 个单元，无需新增。`,
      );
    },
    onError: (cause) => setSeedNotice(toDisplayMessage(cause)),
  });

  function refreshCatalog() {
    void queryClient.invalidateQueries({ queryKey: ['textbooks'] });
  }

  const totalArticles = nodes.reduce(
    (sum, node) => sum + node.articleCount + node.looseArticles.length,
    0,
  );

  return (
    <section className="mx-auto max-w-5xl px-5 py-6 sm:py-10">
      <header>
        {/*
          h1 必须无条件渲染，并且其文本包含「教材目录」。
          e2e/app-shell.spec.ts 会断言的正是这一条；如果把它塞进数据分支里，
          慢环境下（目录还在请求）标题会缺席，测试就会随机失败。
          因此页面结构固定为：h1 → 筛选区 → 内容区，只有内容区随加载/错误/空态切换。
        */}
        <h1 className="text-2xl font-bold">教材目录</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          按「教材 → 单元 → 文章」浏览。平台只内置公开的目录元数据，
          <b>受版权保护的课文正文由具备使用授权的教师或用户导入</b>，导入时须确认授权并留痕。
        </p>
      </header>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {/* 用两个切换按钮而不是 role="tab"：这里没有 tabpanel 关联关系，
            声明成 tab 反而会让读屏用户期待 Tab 键切换面板。 */}
        <div className="inline-flex rounded-md border p-0.5">
          <ViewTab
            active={view === 'tree'}
            onClick={() => setView('tree')}
            icon={<Trees aria-hidden className="size-3.5" />}
          >
            目录
          </ViewTab>
          <ViewTab
            active={view === 'articles'}
            onClick={() => setView('articles')}
            icon={<List aria-hidden className="size-3.5" />}
          >
            全部文章
          </ViewTab>
        </div>

        <div className="ml-auto flex flex-wrap gap-2">
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={() => setCreateTextbookOpen(true)}>
                <FolderPlus aria-hidden className="size-3.5" />
                新建教材
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setUnitTextbookId(undefined);
                  setCreateUnitOpen(true);
                }}
                disabled={nodes.length === 0}
              >
                <Plus aria-hidden className="size-3.5" />
                新建单元
              </Button>
            </>
          )}
          {isAdmin && (
            <Button
              variant="secondary"
              size="sm"
              loading={seedMutation.isPending}
              onClick={() => {
                setSeedNotice(null);
                seedMutation.mutate();
              }}
            >
              <DatabaseZap aria-hidden className="size-3.5" />
              装载内置目录
            </Button>
          )}
          {status === 'authenticated' ? (
            <Button size="sm" onClick={() => setImportOpen(true)}>
              <FileText aria-hidden className="size-3.5" />
              导入文章
            </Button>
          ) : (
            <Button size="sm" onClick={() => navigate(ROUTES.login)}>
              <BookOpen aria-hidden className="size-3.5" />
              登录后导入
            </Button>
          )}
        </div>
      </div>

      {seedNotice && (
        <p role="status" className="mt-3 flex items-start gap-1.5 rounded-md border bg-muted/40 px-3 py-2 text-xs">
          <CheckCircle2 aria-hidden className="mt-0.5 size-3.5 shrink-0 text-primary" />
          {seedNotice}
        </p>
      )}

      <div className="mt-4 rounded-lg border bg-card p-3 text-card-foreground shadow-sm">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
          <Field id="catalog-level" label="适用等级" hint="默认「大学英语三」，改动后自动重新查询">
            <Input
              id="catalog-level"
              value={levelInput}
              onChange={(event) => changeLevel(event.target.value)}
              placeholder={DEFAULT_LEVEL}
            />
          </Field>

          {view === 'tree' && (
            <Field
              id="catalog-search"
              label="搜索教材 / 单元 / 文章标题"
              hint="本地即时过滤，中英文标题都能匹配，不会发起请求"
            >
              <div className="relative">
                <Search
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="catalog-search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="例如：success / 成功 / Unit 3"
                  className="pl-9"
                />
              </div>
            </Field>
          )}
        </div>

        {view === 'tree' && (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge tone="muted">{nodes.length} 套教材</Badge>
            <Badge tone="muted">{totalArticles} 篇正文</Badge>
            {keywordActive && <Badge tone="primary">已按「{keyword.trim()}」过滤</Badge>}
          </p>
        )}
      </div>

      <div className="mt-4">
        {view === 'tree' ? (
          <TreeView
            isPending={catalogQuery.isPending}
            isError={catalogQuery.isError}
            errorMessage={toDisplayMessage(catalogQuery.error)}
            nodes={filteredNodes}
            rawCount={nodes.length}
            keywordActive={keywordActive}
            level={level}
            expandedIds={effectiveExpandedIds}
            onToggle={toggleTextbook}
            onOpenArticle={openArticle}
            onRetry={() => void catalogQuery.refetch()}
            canManage={canManage}
            onImport={() => setImportOpen(true)}
            onCreateTextbook={() => setCreateTextbookOpen(true)}
            onSwitchToArticles={() => setView('articles')}
            isAdmin={isAdmin}
            seedPending={seedMutation.isPending}
            onSeed={() => {
              setSeedNotice(null);
              seedMutation.mutate();
            }}
            loginRequired={status !== 'authenticated'}
          />
        ) : (
          <ArticlesView level={level} onOpenArticle={openArticle} />
        )}
      </div>

      <p className="mt-6 text-xs text-muted-foreground">
        平台不内置受版权保护的教材全文。正文来源、授权责任与权利人下架通道见{' '}
        <button
          type="button"
          onClick={() => navigate(ROUTES.copyright)}
          className="text-primary underline underline-offset-4"
        >
          版权与数据来源
        </button>
        。
      </p>

      <ImportArticleDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        textbooks={nodes}
        defaultTextbookId={unitTextbookId}
        onImported={refreshCatalog}
      />

      <CreateTextbookDialog
        open={createTextbookOpen}
        onClose={() => setCreateTextbookOpen(false)}
        defaultLevel={levelInput.trim() || DEFAULT_LEVEL}
        onCreated={(textbook) => {
          setCreateTextbookOpen(false);
          refreshCatalog();
          // 新建后顺手展开它：否则用户会看到目录里"似乎什么都没发生"
          setExpandedIds((current) => {
            const next = new Set(current ?? []);
            next.add(textbook.id);
            writeExpanded(next);
            return next;
          });
        }}
      />

      <CreateUnitDialog
        open={createUnitOpen}
        onClose={() => setCreateUnitOpen(false)}
        textbooks={nodes}
        defaultTextbookId={unitTextbookId}
        onCreated={() => {
          setCreateUnitOpen(false);
          refreshCatalog();
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// 目录视图
// ---------------------------------------------------------------------------

function TreeView({
  isPending,
  isError,
  errorMessage,
  nodes,
  rawCount,
  keywordActive,
  level,
  expandedIds,
  onToggle,
  onOpenArticle,
  onRetry,
  canManage,
  onImport,
  onCreateTextbook,
  onSwitchToArticles,
  isAdmin,
  seedPending,
  onSeed,
  loginRequired,
}: {
  isPending: boolean;
  isError: boolean;
  errorMessage: string;
  nodes: TextbookCatalogNode[];
  rawCount: number;
  keywordActive: boolean;
  level: string;
  expandedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onOpenArticle: (article: ArticleSummary) => void;
  onRetry: () => void;
  canManage: boolean;
  onImport: () => void;
  onCreateTextbook: () => void;
  /** 本地过滤没命中时，给一条"去全部文章看看"的出口 */
  onSwitchToArticles: () => void;
  isAdmin: boolean;
  seedPending: boolean;
  onSeed: () => void;
  loginRequired: boolean;
}) {
  if (isPending) return <LoadingBlock label="正在读取教材目录…" />;
  if (isError) return <ErrorBlock message={errorMessage} onRetry={onRetry} />;

  if (nodes.length === 0) {
    // 空态分两种：过滤没命中 vs 库本来就是空的。两者该做的事完全不同。
    if (keywordActive && rawCount > 0) {
      return (
        <EmptyBlock
          title="没有匹配的教材、单元或文章"
          description="本地过滤只匹配目录树里的标题。清空搜索框看全部内容，或切到「全部文章」按正文列表找。"
          action={
            <Button variant="outline" onClick={onSwitchToArticles}>
              <List aria-hidden className="size-3.5" />
              去全部文章
            </Button>
          }
        />
      );
    }
    return (
      <EmptyBlock
        title={`还没有「${level || DEFAULT_LEVEL}」的教材目录`}
        description={
          isAdmin
            ? '可以点「装载内置目录」一次性导入公开的教材目录元数据（只有目录，不含正文）。'
            : canManage
              ? '可以新建教材并添加单元，再往单元里导入正文。'
              : '教师或管理员建立目录后，这里会显示单元与文章。'
        }
        action={
          isAdmin ? (
            <Button onClick={onSeed} loading={seedPending}>
              <DatabaseZap aria-hidden className="size-3.5" />
              装载内置目录
            </Button>
          ) : canManage ? (
            <div className="flex flex-wrap justify-center gap-2">
              <Button onClick={onCreateTextbook}>
                <FolderPlus aria-hidden className="size-3.5" />
                新建教材
              </Button>
              <Button variant="outline" onClick={onImport}>
                <FileText aria-hidden className="size-3.5" />
                导入文章
              </Button>
            </div>
          ) : loginRequired ? (
            <p className="text-xs text-muted-foreground">登录后如果你是教师或管理员，可以在这里建立目录。</p>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <TextbookTree
        nodes={nodes}
        expandedIds={expandedIds}
        onToggle={onToggle}
        onOpenArticle={onOpenArticle}
      />
      <div className="mt-3 space-y-2">
        <ArticleEmptyHint text="目录里没有找到这篇正文？它在「全部文章」视图里仍然可见 —— 切换视图即可看到未归入目录的文章。" />
        <div className="flex flex-wrap gap-2">
          {canManage && (
            <Button variant="outline" size="sm" onClick={onImport}>
              <Plus aria-hidden className="size-3.5" />
              往目录里导入正文
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 全部文章视图（分页 + 服务端过滤）
// ---------------------------------------------------------------------------

function ArticlesView({
  level,
  onOpenArticle,
}: {
  level: string;
  onOpenArticle: (article: ArticleSummary) => void;
}) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [onlyLong, setOnlyLong] = useState(false);
  const [scope, setScope] = useState<Scope>('all');

  const params = useMemo(
    () => ({
      page,
      pageSize: ARTICLES_PAGE_SIZE,
      scope,
      level: level || undefined,
      q: q.trim() || undefined,
      onlyLong: onlyLong || undefined,
      // 换筛选条件必须回到第一页：否则过滤后 totalPages 变小，用户会停在空页上
    }),
    [page, q, onlyLong, scope, level],
  );

  const articlesQuery = useQuery({
    queryKey: queryKeys.articles(params),
    queryFn: () => contentApi.articles(params),
    // 同上：登录态一致性问题由 api/client 统一处理
  });

  /**
   * 「只看长难句」在本地再过滤一次。
   *
   * 原因：这个筛选依赖 `longSentenceCount`，而它是列表聚合出来的派生值，
   * 服务端是否按它过滤会随实现而变。前端过滤能保证"勾上之后看到的一定都有长难句"；
   * 两者叠加不会重复过滤出错误结果（本地过滤是服务端结果的子集）。
   * 取舍：某页若有文章被本地滤掉，那一页会显得不满 —— 可接受，且用户能直接翻页。
   */
  const items = useMemo(() => {
    const list = articlesQuery.data?.items ?? [];
    return onlyLong ? list.filter((article) => article.longSentenceCount > 0) : list;
  }, [articlesQuery.data?.items, onlyLong]);

  function updateFilter(next: () => void) {
    next();
    setPage(1);
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border bg-card p-3 text-card-foreground shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1">
            <Field id="articles-q" label="搜索文章标题" hint="服务端搜索，中英文标题都能匹配">
              <div className="relative">
                <Search
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="articles-q"
                  value={q}
                  onChange={(event) => updateFilter(() => setQ(event.target.value))}
                  placeholder="例如：success"
                  className="pl-9"
                />
              </div>
            </Field>
          </div>

          <div>
            <label htmlFor="articles-scope" className="mb-1.5 block text-sm font-medium">
              范围
            </label>
            <FormSelect
              id="articles-scope"
              value={scope}
              onChange={(event) => updateFilter(() => setScope(event.target.value as Scope))}
              className="h-10 w-full"
            >
              {SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FormSelect>
          </div>

          <label className="flex h-10 items-center gap-2 rounded-md border px-3 text-sm">
            <input
              type="checkbox"
              checked={onlyLong}
              onChange={(event) => updateFilter(() => setOnlyLong(event.target.checked))}
              className="size-4 accent-[var(--primary)]"
            />
            <Filter aria-hidden className="size-3.5 text-muted-foreground" />
            只看含长难句
          </label>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          「我的」= 只列你自己导入的文章（含私有）；「公开」= 公共库；「全部」= 公共库 + 你自己的私有文章。
        </p>
      </div>

      {articlesQuery.isPending ? (
        <LoadingBlock label="正在读取文章列表…" />
      ) : articlesQuery.isError ? (
        <ErrorBlock message={toDisplayMessage(articlesQuery.error)} onRetry={() => void articlesQuery.refetch()} />
      ) : items.length === 0 ? (
        <EmptyBlock
          title={onlyLong ? '当前筛选下没有含长难句的文章' : '没有找到文章'}
          description={
            onlyLong
              ? '取消「只看含长难句」，或换一页看看：长难句数量是导入后由分句流程算出来的。'
              : '可以调整关键词或范围，也可以直接导入一篇正文。'
          }
        />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {items.map((article) => (
            <ArticleCard key={article.id} article={article} onOpen={onOpenArticle} />
          ))}
        </div>
      )}

      <Pagination page={articlesQuery.data?.page ?? page} totalPages={articlesQuery.data?.totalPages ?? 1} onChange={setPage} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 小组件与本地存储
// ---------------------------------------------------------------------------

function ViewTab({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? 'inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground'
          : 'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      }
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * 展开状态存在 localStorage 里（而不是服务端）：它纯粹是本机视角偏好，
 * 换台设备想要不同的展开情况很正常，不值得为它建一张表。
 *
 * 读的时候对 JSON 做防御性校验：localStorage 是用户可改的，
 * 里面出现非数组/含非字符串的内容时应当退回空集合，而不是让整页崩掉。
 */
/**
 * 读展开状态。**返回 `null` 表示"用户从未选择过"**，而不是空集合 ——
 * 这个区别很关键：空集合是用户的真实选择（"我把所有教材都收起来了"），
 * 不能被"默认展开第一本"的兜底逻辑覆盖掉。因此存储里没有值时返回 null。
 */
function readExpanded(): Set<string> | null {
  try {
    const raw = localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((item): item is string => typeof item === 'string'));
  } catch {
    return null;
  }
}

function writeExpanded(ids: ReadonlySet<string>): void {
  try {
    localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // 隐私模式或配额满：展开状态记不住不影响使用，静默忽略
  }
}
